import {ChangeDetectionStrategy, Component, OnInit, signal} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormBuilder, ReactiveFormsModule} from "@angular/forms";
import {ButtonModule} from "primeng/button";
import {InputTextModule} from "primeng/inputtext";
import {ToggleSwitchModule} from "primeng/toggleswitch";
import {MessageService} from "primeng/api";
import {finalize} from "rxjs";
import {ConfigService} from "../../services/config.service";

@Component({
  selector: 'app-config-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule
  ],
  templateUrl: './config-tab.component.html',
  styleUrl: './config-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigTabComponent implements OnInit {
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);

  protected readonly form = this.fb.group({
    lotteryClosedMappingStartDate: [''],
    marketingSmsEnabled: [true],
    nycAccountAccessEnabled: [true],
    checkoutCouponsEnabled: [false],
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly configService: ConfigService,
    private readonly messageService: MessageService
  ) {
  }

  ngOnInit(): void {
    this.reload();
  }

  reload() {
    this.loading.set(true);
    this.configService.getConfig().pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: config => {
        this.form.patchValue({
          lotteryClosedMappingStartDate: toDateInputValue(config.lotteryClosedMappingStartDate),
          marketingSmsEnabled: config.marketingSmsEnabled,
          nycAccountAccessEnabled: config.nycAccountAccessEnabled,
          checkoutCouponsEnabled: config.checkoutCouponsEnabled,
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to load config'
        });
      }
    });
  }

  save() {
    if (this.saving()) return;
    this.saving.set(true);
    const rawValue = this.form.controls.lotteryClosedMappingStartDate.value;
    const command = {
      lotteryClosedMappingStartDate: fromDateInputValue(rawValue),
      marketingSmsEnabled: this.form.controls.marketingSmsEnabled.value ?? true,
      nycAccountAccessEnabled: this.form.controls.nycAccountAccessEnabled.value ?? true,
      checkoutCouponsEnabled: this.form.controls.checkoutCouponsEnabled.value ?? false,
    };

    this.configService.updateConfig(command).pipe(
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Config updated'
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to update config'
        });
      }
    });
  }
}

function toDateInputValue(value: string | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

function fromDateInputValue(value: string | null): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}
